import { Button } from "@/components/custom/button";
import { disableLinksInPreview } from "@/lib/email-preview";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { client } from "@/client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import CronInput from "@/components/custom/cron-input.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Route } from "@/routes/dashboard/reports/$reportId/route.tsx";
import { getTimeZone, useTimeSettings } from "@/state/time-settings.tsx";
import React, { useMemo } from "react";
import SelectInputCreatable from "@/components/select-input/select-input-creatable";
import { TzPicker } from "@/routes/dashboard/-tz-picker.tsx";
import { MdWarning } from "react-icons/md";
import {
  DialogPage,
  MultiStepDialogContent,
} from "@/components/custom/paginated-dialog-content/multi-step-dialog-content.tsx";
import { usePermissions } from "@/hooks/use-permissions";
import { Loader } from "@/components/custom/loader.tsx";
import {
  parseQuestionRef,
  resolveQuestion,
  resolveQuestions,
  questionsToOptions,
} from "./-question-utils";
import { CheckboxGroupInput } from "@/components/custom/form-inputs/checkbox-group";
import { TextArea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { showActionErrorToast } from "@/state/error-toast";
import { showEmailOutcomeToast } from "@/state/email-outcome-toast";

const optionSchema = z.object({
  label: z.string(),
  value: z.string(),
});

const createCustomReportFormSchema = (t: (key: string) => string) =>
  z.object({
    reportName: z.string().min(1, { message: t("pleaseEnterReportName") }),
    mailingList: z
      .array(optionSchema)
      .min(1, { message: t("pleaseAddAtLeastOneEmail") }),
    internalMailingList: z.array(optionSchema).optional(),
    approvers: z.array(optionSchema).optional(),
    summaryText: z.string().optional(),
    customReportQuestions: z
      .array(z.string())
      .max(4, { message: t("youCanSelectUpTo4Questions") })
      .optional(),
    summaryTable: z.string().optional(),
    addAttachments: z.boolean().optional(),
    fileType: z.string().optional(),
    selectedQuestions: z.array(z.string()).optional(),
    schedule: z.string().min(1, { message: t("pleaseSelectSchedule") }),
    timezone: z
      .string()
      .min(1, { message: t("pleaseSelectTimezone") })
      .optional(),
  });

interface CreateCustomSubscriptionProps {
  filters: string;
  onClose?: () => void;
}

export const CreateCustomSubscription = ({
  filters,
  onClose,
}: CreateCustomSubscriptionProps) => {
  const { reportId } = Route.useRouteContext();
  const timeSettings = useTimeSettings();
  const { t } = useTranslation();
  const { permissions } = usePermissions();
  const canAddAttachments = permissions.reports.subscription.addAttachments;
  const [currentStep, setCurrentStep] = React.useState(0);
  const [showPreview, setShowPreview] = React.useState(false);

  const params = new URLSearchParams(filters);
  const dateRangeRegex = /^\d{4}-\d{2}-\d{2}(~\d{4}-\d{2}-\d{2})?$/;

  const isAbsDates = Array.from(params.entries()).some(([key, value]) => {
    return key.includes("date") && dateRangeRegex.test(value ?? "");
  });

  const subscribe = useMutation({
    ...client.emailReports.createSchedule.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
    onSuccess: () => toast.success(t("successfullyCreatedSubscription")),
  });

  const testSubscription = useMutation({
    ...client.reporting.sendTestReport.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
    onSuccess: (outcome) =>
      showEmailOutcomeToast(outcome, {
        partial: t("testEmailsPartiallyFailed"),
        success: t("mailSent"),
      }),
  });

  const customReportFormSchema = useMemo(
    () => createCustomReportFormSchema(t),
    [t],
  );

  const form = useForm<z.infer<typeof customReportFormSchema>>({
    resolver: zodResolver(customReportFormSchema),
    defaultValues: {
      reportName: "",
      mailingList: [],
      internalMailingList: [],
      summaryText: "",
      customReportQuestions: [],
      summaryTable: undefined,
      addAttachments: false,
      fileType: "xlsx",
      selectedQuestions: [],
      schedule: "0 9 * * MON",
      timezone: getTimeZone("local"),
    },
  });

  const reportName = form.watch("reportName");
  const mailingList = form.watch("mailingList");
  const internalMailingList = form.watch("internalMailingList");
  const summaryText = form.watch("summaryText");
  const customReportQuestions = form.watch("customReportQuestions");
  const summaryTable = form.watch("summaryTable");
  const addAttachments = form.watch("addAttachments");
  const fileType = form.watch("fileType");
  const selectedQuestions = form.watch("selectedQuestions");

  const { data: customReportMetadata } = useQuery({
    ...client.reporting.getReportMetadata.asQueryOptions({
      id: reportId,
      questionTypes: ["scalar", "gauge", "pivot", "table"],
    }),
  });

  const numberQuestions = questionsToOptions(
    customReportMetadata?.questions.filter((q) =>
      ["scalar", "gauge"].includes(q.type),
    ) ?? [],
  );

  const tableQuestions = questionsToOptions(
    customReportMetadata?.questions.filter((q) =>
      ["pivot", "table"].includes(q.type),
    ) ?? [],
  );

  // Default values object - will be provided by backend in the future
  const defaultValues = {
    // total detected loss,
    // confirmed transactions with detected loss,
    // alert accuracy,
    // product recovered percentage
    questionIds: [3156, 3174, 3184, 3133] as number[],
    summaryTableId: 3178,
    summaryText:
      "The overall confirmed Non-Scan rate in the {} stores with alerting enabled is {},  up from the {} last week. The total financial impact for the week across those stores was {}$ with a recovery rate of {}%.",
  };

  // Track if defaults have been set to avoid re-setting when user clears values
  const [defaultsSet, setDefaultsSet] = React.useState(false);

  // Set default values for customReportQuestions and summaryTable based on question IDs
  React.useEffect(() => {
    if (customReportMetadata?.questions && !defaultsSet) {
      console.log("Setting defaults, defaultsSet:", defaultsSet);
      // Set default number questions if not already set
      if (
        !customReportQuestions?.length &&
        defaultValues.questionIds.length > 0
      ) {
        const matchedQuestions = numberQuestions
          .filter((q) =>
            defaultValues.questionIds.includes(parseQuestionRef(q.value).id),
          )
          .map((q) => q.value);

        if (matchedQuestions.length > 0) {
          form.setValue("customReportQuestions", matchedQuestions);
        }
      }

      // Set default summary table if not already set
      if (!summaryTable && defaultValues.summaryTableId) {
        const matchedTable = tableQuestions.find(
          (q) => parseQuestionRef(q.value).id === defaultValues.summaryTableId,
        );

        if (matchedTable) {
          console.log("Setting default summaryTable to:", matchedTable.value);
          form.setValue("summaryTable", matchedTable.value);
        }
      }

      // Set default summary text if not already set
      if (!summaryText && defaultValues.summaryText) {
        form.setValue("summaryText", defaultValues.summaryText);
      }

      setDefaultsSet(true);
    }
  }, [customReportMetadata, numberQuestions, tableQuestions, defaultsSet]);

  const previewQuery = useQuery({
    ...client.reporting.previewCustomReport.asQueryOptions({
      requestBody: {
        reportId,
        filters,
        summaryText:
          summaryText || "This is a preview of your custom report email.",
        mailingList: mailingList?.map((m) => m.value) || [
          "preview@example.com",
        ],
        reportName: reportName || "Custom Report Preview",
        customReportQuestions: resolveQuestions(
          customReportQuestions,
          customReportMetadata?.questions,
        ),
        summaryTable:
          resolveQuestion(summaryTable, customReportMetadata?.questions) ??
          undefined,
      },
    }),
    enabled: false,
  });

  const handleGeneratePreview = async () => {
    setShowPreview(true);
    await previewQuery.refetch();
  };

  const onSubmit = async (data: z.infer<typeof customReportFormSchema>) => {
    // Parse and log the detailed question data
    const parsedCustomQuestions = resolveQuestions(
      data.customReportQuestions,
      customReportMetadata?.questions,
    );

    const parsedSummaryTable =
      resolveQuestion(data.summaryTable, customReportMetadata?.questions) ??
      undefined;

    const parsedSelectedQuestions = resolveQuestions(
      data.selectedQuestions,
      customReportMetadata?.questions,
    );

    const body = {
      name: "Next Email Report",
      cronStart: data?.schedule,
      timezone: data?.timezone ?? timeSettings.timeZone ?? "UTC",
      data: {
        reportName: data.reportName,
        mailingList: data.mailingList?.map((option) => option.value),
        internalMailingList: data.internalMailingList?.map(
          (option) => option.value,
        ),
        approvers: data.approvers?.map((option) => option.value),
        reportId,
        reportUrl: filters,
        summaryText: data.summaryText,
        isCustomReport: true,
        customReportQuestions: parsedCustomQuestions,
        summaryTable: parsedSummaryTable,
        addAttachments: data.addAttachments,
        fileType: data.fileType,
        selectedQuestions: parsedSelectedQuestions,
      },
    };
    console.log("Full form data:", body);

    try {
      await subscribe.mutateAsync({
        requestBody: body,
      });
      onClose?.();
    } catch (error) {
      console.error("Failed to create subscription:", error);
      // stay open
    }
  };

  const sendTestMail = async () => {
    const formValues = form.getValues();
    const parsedSelectedQuestions = resolveQuestions(
      selectedQuestions,
      customReportMetadata?.questions,
    );

    const body = {
      mailingList: mailingList.map((option) => option.value),
      internalMailingList: internalMailingList?.map((option) => option.value),
      approvers: formValues.approvers?.map((option) => option.value),
      reportName,
      reportId,
      filters,
      summaryText: summaryText,
      isCustomReport: true,
      customReportQuestions: resolveQuestions(
        customReportQuestions,
        customReportMetadata?.questions,
      ),
      summaryTable:
        resolveQuestion(summaryTable, customReportMetadata?.questions) ??
        undefined,
      addAttachments: addAttachments,
      fileType: fileType,
      selectedQuestions: parsedSelectedQuestions,
    };
    await testSubscription.mutateAsync({
      requestBody: body,
    });
  };

  const emailQueryResult = useQuery(
    client.users.listEmails.asQueryOptions({
      limit: 1000,
      page: 1,
    }),
  );
  const emailOptions = React.useMemo(
    () =>
      (emailQueryResult.data?.results ?? []).map((value: string) => ({
        value,
        label: value,
      })),
    [emailQueryResult.data?.results],
  );
  // Internal recipients are offered from the users who could approve (ID-7165): approvers must be
  // picked from among them, and an approver without customSubscription.approve can't open the review.
  // Typing any other address still works.
  const approverCandidatesQuery = useQuery(
    client.emailReports.listApproverCandidateEmails.asQueryOptions(),
  );
  const internalEmailOptions = React.useMemo(
    () =>
      (approverCandidatesQuery.data ?? []).map((value: string) => ({
        value,
        label: value,
      })),
    [approverCandidatesQuery.data],
  );

  const Page1 = useMemo(
    () => (
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="reportName"
          render={({ field }) => (
            <FormItem className="py-4">
              <FormLabel>{t("name")}</FormLabel>
              <FormControl>
                <Input placeholder={t("enterName")} {...field} />
              </FormControl>
              <FormMessage>
                {form.formState.errors.reportName?.message}
              </FormMessage>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="mailingList"
          render={({ field }) => (
            <FormItem className="py-4">
              <FormLabel>{t("inviteeList")}</FormLabel>
              <FormControl>
                <SelectInputCreatable
                  {...field}
                  isMulti={true}
                  options={emailOptions}
                  createLabel={t("invite")}
                  placeholder={t("typeEmails")}
                />
              </FormControl>
              <FormMessage>
                {form.formState.errors.mailingList?.message}
              </FormMessage>
            </FormItem>
          )}
        />
      </div>
    ),
    [form.control, t, emailOptions],
  );

  const Page2 = useMemo(
    () => (
      <div className="space-y-4">
        <FormField
          key={`schedule-${currentStep}`}
          control={form.control}
          name="schedule"
          render={({ field }) => (
            <FormItem className="py-4">
              <FormLabel>{t("schedule")}</FormLabel>
              <FormControl>
                <CronInput className="gap-4" {...field} />
              </FormControl>
              <FormMessage>
                {form.formState.errors.schedule?.message}
              </FormMessage>
            </FormItem>
          )}
        />
        <div className={"flex items-center gap-4"}>
          <label className="text-nowrap">{t("atTimezone")}</label>
          <FormField
            key={`timezone-${currentStep}`}
            control={form.control}
            name="timezone"
            render={({ field }) => (
              <TzPicker
                className="w-full"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </div>
    ),
    [form.control, t, currentStep],
  );

  const Page3 = useMemo(
    () => (
      <div className="space-y-6">
        <div className="border-l-4 border-azure bg-azure/5 p-4 rounded-r">
          <h3 className="font-semibold text-lg mb-2">
            {t("emailApprovalWorkflow")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("configureEmailApprovalWorkflowDescription")}
          </p>
        </div>

        <FormField
          control={form.control}
          name="internalMailingList"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("internalRecipients")}</FormLabel>
              <p className="text-sm text-muted-foreground mb-2">
                {t("internalRecipientsDescription")}
              </p>
              <FormControl>
                <SelectInputCreatable
                  {...field}
                  isMulti={true}
                  options={internalEmailOptions}
                  createLabel={t("add")}
                  placeholder={t("typeEmailAddresses")}
                />
              </FormControl>
              <FormMessage>
                {form.formState.errors.internalMailingList?.message}
              </FormMessage>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="approvers"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("approvers")}</FormLabel>
              <p className="text-sm text-muted-foreground mb-2">
                {t("approversDescription")}
              </p>
              <FormControl>
                <SelectInputCreatable
                  {...field}
                  value={field.value || []}
                  onChange={(options) => field.onChange(options || [])}
                  isMulti={true}
                  options={
                    internalMailingList?.map((email) => ({
                      value: email.value,
                      label: email.label,
                    })) || []
                  }
                  placeholder={t("selectApprovers")}
                  isDisabled={
                    !internalMailingList || internalMailingList.length === 0
                  }
                />
              </FormControl>
              {(!internalMailingList || internalMailingList.length === 0) && (
                <p className="text-sm text-muted-foreground mt-1">
                  {t("addInternalRecipientsFirstToSelectApprovers")}
                </p>
              )}
              <FormMessage>
                {form.formState.errors.approvers?.message}
              </FormMessage>
            </FormItem>
          )}
        />

        <div>
          <FormLabel>{t("externalRecipients")}</FormLabel>
          <p className="text-sm text-muted-foreground mb-2">
            {t("externalRecipientsDescription")}
          </p>
          <div className="border rounded-md p-3 bg-muted/30">
            {mailingList && mailingList.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {mailingList.map((email) => (
                  <span
                    key={email.value}
                    className="bg-azure/10 text-azure px-2 py-1 rounded text-sm"
                  >
                    {email.label}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("noExternalRecipientsSelected")}
              </p>
            )}
          </div>
        </div>

        {canAddAttachments && (
          <>
            <hr />

            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded-r">
                <h3 className="font-semibold text-lg mb-2">
                  {t("attachResults")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("attachResultsDescription")}
                </p>
              </div>

              <FormField
                control={form.control}
                name="addAttachments"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {t("attachResults")}
                      </FormLabel>
                      <p className="text-sm text-muted-foreground">
                        {t("attachResultsAsFilesDescription")}
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {addAttachments && (
                <>
                  <FormField
                    control={form.control}
                    name="fileType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("fileType")}</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={t("selectFileType")} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                            <SelectItem value="csv">CSV (.csv)</SelectItem>
                            <SelectItem value="json">JSON (.json)</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="selectedQuestions"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("selectQuestionsForAttachment")}
                        </FormLabel>
                        <p className="text-sm text-muted-foreground mb-2">
                          {t("selectQuestionsForAttachmentDescription")}
                        </p>
                        <FormControl>
                          <CheckboxGroupInput
                            {...field}
                            options={[...numberQuestions, ...tableQuestions]}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}
            </div>
          </>
        )}

        <div className="bg-muted/30 p-4 rounded-md">
          <h4 className="font-medium mb-2">{t("howItWorks")}</h4>
          <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
            <li>{t("reportSentToInternalRecipientsAndApprovers")}</li>
            <li>{t("approversReceiveEmailWithApprovalButton")}</li>
            <li>{t("onceApprovedReportSentToExternalRecipients")}</li>
          </ol>
        </div>
      </div>
    ),
    [
      form.control,
      emailOptions,
      internalEmailOptions,
      mailingList,
      numberQuestions,
      tableQuestions,
      canAddAttachments,
      addAttachments,
      t,
    ],
  );

  const Page4 = useMemo(
    () => (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left side - Form fields */}
        <div className="flex flex-col min-h-0 space-y-4">
          <FormField
            control={form.control}
            name="summaryText"
            render={({ field }) => (
              <FormItem className="flex-1 flex flex-col min-h-0">
                <FormLabel>{t("summaryTextOptional")}</FormLabel>
                <p className="text-sm text-muted-foreground mb-2">
                  {t("addCustomMessageToReportEmail")}
                </p>
                <FormControl className="flex-1 min-h-0">
                  <TextArea
                    {...field}
                    placeholder={t("enterSummaryText")}
                    className="resize-none h-full min-h-[150px]"
                  />
                </FormControl>
                <FormMessage>
                  {form.formState.errors.summaryText?.message}
                </FormMessage>
              </FormItem>
            )}
          />

          <div className="flex-shrink-0 space-y-2">
            <Accordion type="single" collapsible>
              <AccordionItem value="metrics">
                <AccordionTrigger className="text-sm font-medium">
                  {t("selectMetricsUpTo4")} (
                  {customReportQuestions?.length || 0}/4)
                </AccordionTrigger>
                <AccordionContent>
                  <div className="overflow-y-auto max-h-[200px] pt-2">
                    <FormField
                      control={form.control}
                      name="customReportQuestions"
                      render={({ field }) => (
                        <FormItem>
                          <p className="text-sm text-muted-foreground mb-2">
                            {t("selectUpTo4MetricsDescription")}
                          </p>
                          <FormControl>
                            <CheckboxGroupInput
                              {...field}
                              options={numberQuestions}
                              maxSelections={4}
                            />
                          </FormControl>
                          <FormMessage>
                            {
                              form.formState.errors.customReportQuestions
                                ?.message
                            }
                          </FormMessage>
                        </FormItem>
                      )}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion type="single" collapsible>
              <AccordionItem value="table">
                <AccordionTrigger className="text-sm font-medium">
                  {t("summaryTable")} ({summaryTable ? 1 : 0}/1)
                </AccordionTrigger>
                <AccordionContent>
                  <div className="overflow-y-auto max-h-[200px] pt-2">
                    <FormField
                      control={form.control}
                      name="summaryTable"
                      render={({ field }) => (
                        <FormItem>
                          <p className="text-sm text-muted-foreground mb-2">
                            {t("selectOnePivotOrTableForSummary")}
                          </p>
                          <FormControl>
                            <CheckboxGroupInput
                              options={tableQuestions}
                              maxSelections={1}
                              value={summaryTable ? [summaryTable] : []}
                              onChange={(values) => {
                                const newValue =
                                  values.length > 0 ? values[0] : undefined;
                                field.onChange(newValue);
                              }}
                            />
                          </FormControl>
                          <FormMessage>
                            {form.formState.errors.summaryTable?.message}
                          </FormMessage>
                        </FormItem>
                      )}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        {/* Right side - Preview */}
        <div className="flex flex-col min-h-0 space-y-4">
          <div className="flex-shrink-0">
            <h3 className="font-semibold text-lg">{t("emailPreview")}</h3>
            <p className="text-sm text-muted-foreground">
              {t("emailPreviewDescription")}
            </p>
          </div>
          <div className="border rounded-lg overflow-hidden bg-gray-50 flex-1 min-h-[300px] max-h-[600px]">
            {!showPreview ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <p className="mb-4">{t("clickGeneratePreviewToSeeEmail")}</p>
              </div>
            ) : previewQuery.isFetching ? (
              <div className="flex items-center justify-center h-full">
                <Loader />
              </div>
            ) : previewQuery.data ? (
              <iframe
                srcDoc={disableLinksInPreview(previewQuery.data)}
                className="w-full h-full border-0"
                title={t("emailPreview")}
                sandbox="allow-same-origin"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                {t("failedToLoadPreview")}
              </div>
            )}
          </div>
        </div>
      </div>
    ),
    [
      previewQuery.data,
      previewQuery.isLoading,
      form.control,
      numberQuestions,
      tableQuestions,
    ],
  );

  const footerButtons = useMemo(
    () => (
      <div className="flex gap-2">
        <Button
          type="button"
          onClick={handleGeneratePreview}
          disabled={previewQuery.isFetching}
          variant="outline"
        >
          {previewQuery.isFetching ? (
            <>
              <Loader size="xs" className="mr-2" />
              {t("generating")}
            </>
          ) : (
            t("generatePreview")
          )}
        </Button>
        <Button
          type="button"
          onClick={sendTestMail}
          variant="azureOutline"
          disabled={testSubscription.isPending}
        >
          {testSubscription.isPending && <Loader size="xs" />}
          {t("sendMailNow")}
        </Button>
      </div>
    ),
    [
      handleGeneratePreview,
      previewQuery.isFetching,
      sendTestMail,
      testSubscription.isPending,
      t,
    ],
  );

  const pages: DialogPage[] = useMemo(
    () =>
      [
        {
          title: t("overview"),
          content: Page1,
          fields: ["reportName", "mailingList"],
        },
        {
          title: t("scheduler"),
          content: Page2,
          fields: ["schedule", "timezone"],
        },
        {
          title: t("additionalOptions"),
          content: Page3,
          fields: [
            "internalMailingList",
            "approvers",
            ...(canAddAttachments
              ? ["addAttachments", "fileType", "selectedQuestions"]
              : []),
          ],
        },
        {
          title: t("emailTemplate"),
          content: Page4,
          fields: ["customReportQuestions", "summaryTable", "summaryText"],
          footer: footerButtons,
        },
      ].filter(Boolean) as DialogPage[],
    [Page1, Page2, Page3, Page4, t, footerButtons, canAddAttachments],
  );

  const handleNext = React.useCallback(async () => {
    const fields = pages[currentStep].fields;
    const isStepValid = await form.trigger(
      fields as (keyof z.infer<typeof customReportFormSchema>)[],
      { shouldFocus: true },
    );

    if (isStepValid && !isAbsDates) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, pages, form, isAbsDates]);

  const handleBack = React.useCallback(() => {
    setCurrentStep((prev) => prev - 1);
  }, []);

  return (
    <>
      <Form {...form}>
        {isAbsDates && (
          <div className="m-4 p-4 rounded-lg bg-warning text-warning-foreground">
            <MdWarning className="inline-block mr-2 h-5 w-5" />
            {t("absDatesWarning")}
          </div>
        )}
        <form className="flex flex-col flex-1 min-h-0">
          <MultiStepDialogContent
            title={t("createCustomSubscription")}
            pages={pages}
            currentStep={currentStep}
            onNext={handleNext}
            onBack={handleBack}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex-1 min-h-0"
          />
        </form>
      </Form>
    </>
  );
};
